<template>
  <v-card :class="['glass-card-theme', isDarkMode ? 'dark-theme' : 'light-theme']" style="border-radius: 16px; overflow: hidden">
    <!-- Header -->
    <div class="dialog-header d-flex align-center justify-space-between pa-5">
      <div class="d-flex align-center">
        <v-icon color="green" class="mr-3" large>mdi-calendar-clock</v-icon>
        <div>
          <h3 class="text-h6 font-weight-black mb-0" :class="isDarkMode ? 'white--text' : 'black--text'">
            {{ shift._id ? "Schicht bearbeiten" : "Schicht erstellen" }}
          </h3>
          <span class="text-caption grey--text">Konfigurieren Sie Arbeitszeiten und Pausen</span>
        </div>
      </div>
      <v-btn icon :dark="isDarkMode" @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>

    <v-divider class="border-light"></v-divider>

    <!-- Content -->
    <v-card-text class="pa-6 custom-scrollbar" style="max-height: 65vh; overflow-y: auto">
      <!-- Title & Short Name -->
      <v-row class="mb-2">
        <v-col cols="12" md="6" class="py-1">
          <v-text-field
            v-model="shift.name"
            label="Schichtname / Titel"
            placeholder="z.B. Frühschicht"
            outlined
            dense
            hide-details
            :dark="isDarkMode"
            class="rounded-lg"></v-text-field>
        </v-col>
        <v-col cols="12" md="6" class="py-1">
          <v-text-field
            v-model="shift.shortName"
            label="Kurzbezeichnung"
            placeholder="z.B. Früh"
            outlined
            dense
            hide-details
            :dark="isDarkMode"
            class="rounded-lg"></v-text-field>
        </v-col>
      </v-row>

      <!-- Color Selection -->
      <v-row class="mb-2">
        <v-col cols="12" class="py-1">
          <div class="text-caption font-weight-bold grey--text text-uppercase tracking-wide mb-2">Farbe wählen</div>
          <div class="d-flex flex-wrap gap-2 py-1">
            <div
              v-for="color in presetColors"
              :key="color"
              @click="shift.color = color"
              class="color-bubble cursor-pointer d-flex align-center justify-center"
              :style="{
                background: color,
                border: shift.color === color ? '2px solid white' : '2px solid transparent',
                boxShadow: shift.color === color ? '0 0 8px ' + color : 'none',
              }">
              <v-icon v-if="shift.color === color" x-small color="white">mdi-check</v-icon>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- Flags -->
      <v-row class="mb-2">
        <v-col cols="6" class="py-1">
          <v-checkbox v-model="shift.isOpeningShift" label="Öffnungsschicht" hide-details :dark="isDarkMode" color="green" class="mt-0"></v-checkbox>
        </v-col>
        <v-col cols="6" class="py-1">
          <v-checkbox v-model="shift.isClosingShift" label="Schliessschicht" hide-details :dark="isDarkMode" color="green" class="mt-0"></v-checkbox>
        </v-col>
      </v-row>

      <v-divider class="border-light my-4"></v-divider>

      <!-- Zeiteinträge Header -->
      <div class="d-flex justify-space-between align-center mb-3">
        <div class="text-caption font-weight-bold grey--text text-uppercase tracking-wide">Schicht-Zeiten</div>
        <v-btn small outlined color="green" class="rounded-lg font-weight-bold" @click="addTimeSlot">
          <v-icon left small>mdi-plus</v-icon> Zeit hinzufügen
        </v-btn>
      </div>

      <!-- Zeiteinträge Rows -->
      <div class="time-slots-container d-flex flex-column gap-3">
        <div
          v-for="(time, index) in shift.times"
          :key="index"
          class="glass-panel-light pa-3 rounded-lg d-flex align-center justify-space-between flex-wrap gap-2">
          <div class="d-flex align-center flex-grow-1 gap-2" style="min-width: 250px">
            <!-- Start Time -->
            <v-text-field
              v-model="time.start"
              type="time"
              label="Startzeit"
              outlined
              dense
              hide-details
              :dark="isDarkMode"
              class="rounded-lg slot-time-input"></v-text-field>

            <!-- End Time -->
            <v-text-field
              v-model="time.end"
              type="time"
              label="Endzeit"
              outlined
              dense
              hide-details
              :dark="isDarkMode"
              class="rounded-lg slot-time-input"></v-text-field>

            <!-- Typ -->
            <v-select
              v-model="time.type"
              :items="['Arbeitszeit', 'Pause']"
              label="Typ"
              outlined
              dense
              hide-details
              :dark="isDarkMode"
              class="rounded-lg slot-type-select"></v-select>
          </div>

          <v-btn icon color="red" class="hover-red rounded-lg" @click="removeTimeSlot(index)">
            <v-icon>mdi-delete-outline</v-icon>
          </v-btn>
        </div>

        <div v-if="shift.times.length === 0" class="text-center py-6 grey--text text-caption font-italic">
          Keine Zeiten hinzugefügt. Klicken Sie oben auf "Zeit hinzufügen".
        </div>
      </div>

      <!-- Alert Panel for Errors/Warnings -->
      <div v-if="validation.errors.length > 0 || validation.warnings.length > 0" class="mt-4">
        <!-- Error Alerts -->
        <v-alert v-for="(err, idx) in validation.errors" :key="'err-' + idx" type="error" outlined dense class="rounded-lg mb-2 text-caption">
          <div class="d-flex align-center justify-space-between w-100">
            <span>{{ err }}</span>
            <v-btn v-if="err.includes('überschneidet sich')" x-small color="error" class="ml-2 font-weight-bold" @click="autoCorrect">
              Aufteilen
            </v-btn>
          </div>
        </v-alert>

        <!-- Warning Alerts -->
        <v-alert v-for="(warn, idx) in validation.warnings" :key="'warn-' + idx" type="warning" outlined dense class="rounded-lg mb-2 text-caption">
          <div class="d-flex align-center justify-space-between w-100">
            <span>{{ warn }}</span>
            <v-btn v-if="warn.includes('Lücke')" x-small color="warning" class="ml-2 font-weight-bold" @click="autoCorrect"> Lücke füllen </v-btn>
          </div>
        </v-alert>
      </div>

      <v-divider class="border-light my-4"></v-divider>

      <!-- Summary Block -->
      <v-row class="summary-block py-2 rounded-lg mx-0" style="background: rgba(128, 128, 128, 0.05)">
        <v-col cols="6" class="text-center">
          <div class="text-caption grey--text">Gesamte Arbeitszeit</div>
          <div class="text-h6 font-weight-black green--text">{{ formatTime(totalArbeitszeit) }}</div>
        </v-col>
        <v-col cols="6" class="text-center" style="border-left: 1px solid rgba(128, 128, 128, 0.15)">
          <div class="text-caption grey--text">Gesamte Pausenzeit</div>
          <div class="text-h6 font-weight-black orange--text">{{ formatTime(totalPausenzeit) }}</div>
        </v-col>
      </v-row>
    </v-card-text>

    <!-- Footer Actions -->
    <v-divider class="border-light"></v-divider>
    <v-card-actions class="pa-4 bg-actions d-flex justify-space-between">
      <v-btn v-if="shift._id" text color="red lighten-1" class="font-weight-bold rounded-lg px-3" @click="deleteShift">
        <v-icon left>mdi-delete</v-icon> Löschen
      </v-btn>
      <div v-else></div>

      <div class="d-flex gap-2">
        <v-btn text color="grey" class="font-weight-bold rounded-lg px-4" @click="$emit('close')"> Abbrechen </v-btn>
        <v-btn color="green" class="white--text font-weight-bold rounded-lg px-6" :disabled="validation.errors.length > 0" @click="handleSave">
          <v-icon left>mdi-content-save</v-icon> Speichern
        </v-btn>
      </div>
    </v-card-actions>
  </v-card>
</template>

<script>
export default {
  name: "shiftDialog",
  props: {
    existingValue: {
      type: Object,
      default: () => ({
        _id: null,
      }),
    },
  },
  data() {
    return {
      shift: {
        name: "",
        shortName: "",
        times: [],
        color: "",
        isOpeningShift: false,
        isClosingShift: false,
      },
      presetColors: [
        // Reds / Pinks
        "#EF5350",
        "#E53935",
        "#C62828",
        "#EC407A",
        "#D81B60",
        "#AD1457",
        // Purples
        "#AB47BC",
        "#8E24AA",
        "#6A1B9A",
        "#7E57C2",
        "#5E35B1",
        "#4527A0",
        // Indigos / Blues
        "#5C6BC0",
        "#3949AB",
        "#283593",
        "#42A5F5",
        "#1E88E5",
        "#1565C0",
        // Light Blues / Cyans
        "#29B6F6",
        "#039BE5",
        "#0277BD",
        "#26C6DA",
        "#00ACC1",
        "#00838F",
        // Teals / Greens
        "#26A69A",
        "#00897B",
        "#00695C",
        "#66BB6A",
        "#43A047",
        "#2E7D32",
        // Light Greens / Limes
        "#9CCC65",
        "#7CB342",
        "#558B2F",
        "#D4E157",
        "#C0CA33",
        "#9E9D24",
        // Ambers / Oranges
        "#FFCA28",
        "#FFB300",
        "#FF8F00",
        "#FFA726",
        "#FB8C00",
        "#EF6C00",
        // Deep Oranges / Blue Greys
        "#FF7043",
        "#F4511E",
        "#D84315",
        "#78909C",
        "#546E7A",
        "#37474F",
      ],
    }
  },
  created() {
    if (this.existingValue._id) {
      this.shift = this.$clone(this.existingValue)
      if (this.shift.color && !this.presetColors.includes(this.shift.color)) {
        this.presetColors.push(this.shift.color)
      }
    }
  },
  computed: {
    isDarkMode() {
      return localStorage.getItem("shiftManagementDarkMode") !== "false"
    },
    // Berechnet die Gesamtarbeitszeit in Minuten
    totalArbeitszeit() {
      let total = 0
      this.shift.times.forEach((slot) => {
        if (slot.type === "Arbeitszeit" && slot.start && slot.end) {
          total += this.calculateDuration(slot.start, slot.end)
        }
      })
      return total
    },
    // Berechnet die Gesamtpausenzeit in Minuten
    totalPausenzeit() {
      let total = 0
      this.shift.times.forEach((slot) => {
        if (slot.type === "Pause" && slot.start && slot.end) {
          total += this.calculateDuration(slot.start, slot.end)
        }
      })
      return total
    },
    // Real-Time Validation
    validation() {
      let errors = []
      let warnings = []

      // Filter out completely empty rows when analyzing
      let activeSlots = this.shift.times.filter((s) => s.start || s.end)
      let partiallyFilled = activeSlots.some((s) => !s.start || !s.end)

      if (partiallyFilled) {
        errors.push("Bitte Start- und Endzeit für alle ausgefüllten Zeilen eintragen.")
      }

      let parsedSlots = activeSlots
        .filter((s) => s.start && s.end)
        .map((s, index) => {
          let startMin = this.parseTimeToMinutes(s.start)
          let endMin = this.parseTimeToMinutes(s.end)
          let isOvernight = endMin < startMin
          let normalizedEndMin = isOvernight ? endMin + 1440 : endMin
          return { ...s, startMin, endMin: normalizedEndMin, originalIndex: index, isOvernight }
        })

      // Sort chronologically by start time
      parsedSlots.sort((a, b) => a.startMin - b.startMin)

      // 1. Overlaps between same type or work/break
      for (let i = 0; i < parsedSlots.length; i++) {
        for (let j = i + 1; j < parsedSlots.length; j++) {
          let a = parsedSlots[i]
          let b = parsedSlots[j]
          if (a.startMin < b.endMin && b.startMin < a.endMin) {
            if (a.type === "Pause" && b.type === "Pause") {
              errors.push(`Pausen überschneiden sich: ${a.start}-${a.end} und ${b.start}-${b.end}`)
            } else if (a.type === "Arbeitszeit" && b.type === "Arbeitszeit") {
              errors.push(`Arbeitszeiten überschneiden sich: ${a.start}-${a.end} und ${b.start}-${b.end}`)
            } else {
              let work = a.type === "Arbeitszeit" ? a : b
              let pause = a.type === "Pause" ? a : b
              errors.push(`Pause (${pause.start}-${pause.end}) überschneidet sich mit der Arbeitszeit (${work.start}-${work.end}).`)
            }
          }
        }
      }

      // 2. Consecutive slots of the same type touching (e.g. Work ends when next Work starts)
      for (let i = 0; i < parsedSlots.length - 1; i++) {
        let current = parsedSlots[i]
        let next = parsedSlots[i + 1]
        if (current.type === next.type && current.endMin === next.startMin) {
          errors.push(
            `Zwei aufeinanderfolgende ${current.type === "Arbeitszeit" ? "Arbeitszeiten" : "Pausen"} (bis ${current.end} und ab ${
              next.start
            }) sind nicht erlaubt. Bitte verbinden Sie diese zu einem Eintrag.`,
          )
        }
      }

      // 3. A shift must not end with a break
      if (parsedSlots.length > 0) {
        let lastSlot = parsedSlots[parsedSlots.length - 1]
        if (lastSlot.type === "Pause") {
          errors.push("Eine Schicht darf nicht mit einer Pause enden (die Pause muss während der Arbeitszeit sein).")
        }
      }

      // 2. Gaps without breaks (split shift check)
      let gaps = this.getGapsWithoutBreaks()
      if (gaps.length > 0) {
        gaps.forEach((gap) => {
          warnings.push(`Geteilte Schicht: Lücke von ${gap.start} bis ${gap.end} ohne Pause.`)
        })
      }

      // 3. Odd minutes warning (e.g. 08:01 or 12:03)
      let oddTimes = this.hasOddMinutes()
      if (oddTimes.length > 0) {
        oddTimes.forEach((ot) => {
          warnings.push(`Stimmt die Uhrzeit ${ot.time}? Ungewöhnliche Minutenbezeichnung.`)
        })
      }

      return { errors, warnings }
    },
  },
  methods: {
    addTimeSlot() {
      this.shift.times.push({
        start: "",
        end: "",
        type: "Arbeitszeit",
        startMenu: false,
        endMenu: false,
      })
    },
    removeTimeSlot(index) {
      this.shift.times.splice(index, 1)
    },
    // Berechnet die Dauer eines Zeiteintrags in Minuten (Annahme: Format "HH:mm")
    calculateDuration(start, end) {
      const startMin = this.parseTimeToMinutes(start)
      const endMin = this.parseTimeToMinutes(end)
      let diff = endMin - startMin
      if (diff < 0) {
        diff += 24 * 60
      }
      return diff
    },
    parseTimeToMinutes(timeStr) {
      if (!timeStr) return 0
      const [hours, minutes] = timeStr.split(":").map(Number)
      return hours * 60 + minutes
    },
    // Formatiert Minuten in "Xh Ym"
    formatTime(minutes) {
      const hrs = Math.floor(minutes / 60)
      const mins = minutes % 60
      return `${hrs}h ${mins}m`
    },
    getGapsWithoutBreaks() {
      let slots = this.shift.times
        .filter((s) => s.start && s.end)
        .map((s) => {
          let startMin = this.parseTimeToMinutes(s.start)
          let endMin = this.parseTimeToMinutes(s.end)
          if (endMin < startMin) endMin += 1440
          return { ...s, startMin, endMin }
        })

      let workSlots = slots.filter((s) => s.type === "Arbeitszeit").sort((a, b) => a.startMin - b.startMin)
      let breaks = slots.filter((s) => s.type === "Pause")

      let gaps = []
      for (let i = 0; i < workSlots.length - 1; i++) {
        let current = workSlots[i]
        let next = workSlots[i + 1]
        if (current.endMin < next.startMin) {
          let gapStart = current.endMin
          let gapEnd = next.startMin

          // Check if any break covers this gap
          let covered = breaks.some((b) => b.startMin <= gapStart && b.endMin >= gapEnd)
          if (!covered) {
            gaps.push({
              start: current.end,
              end: next.start,
              startMin: gapStart,
              endMin: gapEnd,
            })
          }
        }
      }
      return gaps
    },
    hasOddMinutes() {
      let oddSlots = []
      this.shift.times.forEach((s) => {
        if (s.start) {
          const [h, m] = s.start.split(":").map(Number)
          if (m % 5 !== 0) {
            oddSlots.push({ time: s.start, type: "start" })
          }
        }
        if (s.end) {
          const [h, m] = s.end.split(":").map(Number)
          if (m % 5 !== 0) {
            oddSlots.push({ time: s.end, type: "end" })
          }
        }
      })
      return oddSlots
    },
    resolveOverlaps() {
      let slots = this.shift.times
        .filter((s) => s.start && s.end)
        .map((s) => {
          let startMin = this.parseTimeToMinutes(s.start)
          let endMin = this.parseTimeToMinutes(s.end)
          let isOvernight = endMin < startMin
          if (isOvernight) endMin += 1440
          return { ...s, startMin, endMin, isOvernight }
        })

      let breaks = slots.filter((s) => s.type === "Pause")
      let workSlots = slots.filter((s) => s.type === "Arbeitszeit")

      let resolvedWork = []

      workSlots.forEach((work) => {
        let subSlots = [work]

        breaks.forEach((brk) => {
          let nextSubSlots = []
          subSlots.forEach((ws) => {
            if (ws.startMin < brk.endMin && brk.startMin < ws.endMin) {
              // Overlap exists
              if (ws.startMin < brk.startMin && brk.endMin < ws.endMin) {
                // Break is inside Work -> Split into two work slots
                nextSubSlots.push({
                  startMin: ws.startMin,
                  endMin: brk.startMin,
                  type: "Arbeitszeit",
                  start: ws.start,
                  end: brk.start,
                })
                nextSubSlots.push({
                  startMin: brk.endMin,
                  endMin: ws.endMin,
                  type: "Arbeitszeit",
                  start: brk.end,
                  end: ws.end,
                })
              } else if (brk.startMin <= ws.startMin && brk.endMin < ws.endMin) {
                // Break covers start of Work -> shrink work start
                nextSubSlots.push({
                  startMin: brk.endMin,
                  endMin: ws.endMin,
                  type: "Arbeitszeit",
                  start: brk.end,
                  end: ws.end,
                })
              } else if (ws.startMin < brk.startMin && brk.endMin >= ws.endMin) {
                // Break covers end of Work -> shrink work end
                nextSubSlots.push({
                  startMin: ws.startMin,
                  endMin: brk.startMin,
                  type: "Arbeitszeit",
                  start: ws.start,
                  end: brk.start,
                })
              }
            } else {
              nextSubSlots.push(ws)
            }
          })
          subSlots = nextSubSlots
        })

        resolvedWork.push(...subSlots)
      })

      let finalSlots = [...resolvedWork, ...breaks]
      finalSlots.forEach((s) => {
        if (s.startMin >= 1440) s.startMin -= 1440
        if (s.endMin >= 1440) s.endMin -= 1440
      })

      finalSlots.sort((a, b) => a.startMin - b.startMin)

      this.shift.times = finalSlots.map((s) => ({
        start: s.start,
        end: s.end,
        type: s.type,
        startMenu: false,
        endMenu: false,
      }))
    },
    fillGaps() {
      let gaps = this.getGapsWithoutBreaks()
      gaps.forEach((gap) => {
        this.shift.times.push({
          start: gap.start,
          end: gap.end,
          type: "Pause",
          startMenu: false,
          endMenu: false,
        })
      })

      // Sort times by start time
      this.shift.times.sort((a, b) => {
        return this.parseTimeToMinutes(a.start) - this.parseTimeToMinutes(b.start)
      })
    },
    autoCorrect() {
      this.resolveOverlaps()
      this.fillGaps()
    },
    handleSave() {
      if (this.validation.errors.length > 0) return

      if (this.validation.warnings.length > 0) {
        if (!confirm("Es gibt noch Warnungen (z.B. ungewöhnliche Uhrzeiten). Möchten Sie trotzdem speichern?")) {
          return
        }
      }

      this.saveShift()
    },
    async saveShift() {
      // Clean up completely empty slots before saving
      this.shift.times = this.shift.times.filter((s) => s.start && s.end)
      this.$emit("input", this.shift)
    },
    deleteShift() {
      console.log("[SHIFT_DIALOG] deleteShift clicked", this.shift)
      this.$emit("delete-setting", this.shift)
    },
  },
}
</script>

<style scoped>
.glass-card-theme {
  transition: all 0.3s ease;
}
.glass-card-theme.dark-theme {
  background: rgba(26, 26, 26, 0.98) !important;
  color: #ffffff !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}
.glass-card-theme.light-theme {
  background: rgba(255, 255, 255, 0.98) !important;
  color: #333333 !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}

.dialog-header {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.12), rgba(0, 0, 0, 0));
}
.dark-theme .dialog-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.light-theme .dialog-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.08), rgba(255, 255, 255, 0));
}

.border-light {
  border-color: rgba(255, 255, 255, 0.08) !important;
}
.light-theme .border-light {
  border-color: rgba(0, 0, 0, 0.08) !important;
}

.glass-panel-light {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: all 0.2s ease;
}
.light-theme .glass-panel-light {
  background: rgba(0, 0, 0, 0.02);
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.glass-panel-light:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.12);
}
.light-theme .glass-panel-light:hover {
  background: rgba(0, 0, 0, 0.04);
  border-color: rgba(0, 0, 0, 0.1);
}

.color-bubble {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  transition: all 0.2s ease;
}
.color-bubble:hover {
  transform: scale(1.1);
}

.slot-time-input {
  max-width: 130px;
}
.slot-type-select {
  max-width: 140px;
}

.bg-actions {
  background: rgba(0, 0, 0, 0.1);
}
.light-theme .bg-actions {
  background: rgba(0, 0, 0, 0.02);
}

.hover-red:hover {
  background: rgba(244, 67, 54, 0.1) !important;
}

.gap-2 {
  gap: 8px;
}
.gap-3 {
  gap: 12px;
}

/* Custom Scrollbar */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.12);
  border-radius: 4px;
}
.light-theme .custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.12);
}
</style>
